import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { AppHeader } from "@/components/app/AppHeader";
import { KpiRow } from "@/components/app/KpiRow";
import { useAuth, DEPARTMENT_LABELS } from "@/lib/auth-context";
import { useMySubmissions } from "@/hooks/use-my-submissions";
import { useMyOtherTasks, useMyVideoSubmissions } from "@/hooks/use-editor-tasks";
import { useMyDesignerOtherTasks, useMyDesignerSubmissions } from "@/hooks/use-designer-tasks";
import { useMyAiOtherTasks, useMyAiSubmissions } from "@/hooks/use-ai-tasks";
import { SubmissionModal } from "@/components/submissions/SubmissionModal";
import { TaskUpdateModal } from "@/components/editor/TaskUpdateModal";
import { QuickSubmitCard } from "@/components/editor/QuickSubmitCard";
import { DesignerTaskUpdateModal } from "@/components/designer/DesignerTaskUpdateModal";
import { AiTaskUpdateModal } from "@/components/ai/AiTaskUpdateModal";
import {
  OutputTrendChart,
  monthOutput,
  todayCount,
} from "@/components/submissions/OutputTrendChart";
import { getDepartmentConfig } from "@/lib/department-configs";
import { isCurrentMonth, VIDEO_STATUS_LABEL } from "@/lib/editor-tasks";
import {
  isCurrentMonth as isCurrentMonthDesigner,
  DESIGNER_STATUS_LABEL,
  DESIGNER_CATEGORY_LABEL,
} from "@/lib/designer-tasks";
import {
  useMyScriptwriterOtherTasks,
  useMyScriptwriterSubmissions,
} from "@/hooks/use-scriptwriter-tasks";
import {
  isCurrentMonth as isCurrentMonthScript,
  SCRIPTWRITER_STATUS_LABEL,
} from "@/lib/scriptwriter-tasks";
import { ScriptwriterTaskUpdateModal } from "@/components/scriptwriter/ScriptwriterTaskUpdateModal";
import { useMyMarketerOtherTasks, useMyMarketerTasks } from "@/hooks/use-marketer-tasks";
import { useMySeoOtherTasks, useMySeoTasks } from "@/hooks/use-seo-tasks";
import {
  isCurrentMonth as isCurrentMonthMarketer,
  MARKETER_CATEGORY_LABEL, MARKETER_STATUS_LABEL,
} from "@/lib/marketer-tasks";
import {
  isCurrentMonth as isCurrentMonthSeo,
  SEO_CATEGORY_LABEL, SEO_STATUS_LABEL, durationLabel,
} from "@/lib/seo-tasks";
import { MarketerTaskUpdateModal } from "@/components/marketer/MarketerTaskUpdateModal";
import { SeoTaskUpdateModal } from "@/components/seo/SeoTaskUpdateModal";
import { SmmTaskUpdateModal } from "@/components/smm/SmmTaskUpdateModal";
import { useMySmmOtherTasks, useMySmmTasks } from "@/hooks/use-smm-tasks";
import { SMM_CATEGORY_LABEL, SMM_STATUS_LABEL, aggregateFunnel } from "@/lib/smm-tasks";
import { AdminTaskUpdateModal } from "@/components/admin/AdminTaskUpdateModal";
import { useMyAdminOtherTasks, useMyAdminTasks } from "@/hooks/use-admin-tasks";
import { ADMIN_STATUS_LABEL, isCurrentMonth as isCurrentMonthAdmin, durationFromTimes as adminDur } from "@/lib/admin-tasks";
import { AdminDashboardView } from "@/components/admin/AdminDashboardView";
import { useQuery } from "@tanstack/react-query";
import { getHrKpis } from "@/lib/rpc/hr-kpis.functions";
import { getHrDashboard } from "@/lib/rpc/hr-dashboard.functions";
import { Link } from "@tanstack/react-router";
import { CallingTaskUpdateModal } from "@/components/calling/CallingTaskUpdateModal";
import { useMyCallingOtherTasks, useMyCallingSubmissions } from "@/hooks/use-calling-tasks";
import { CALLING_TYPE_LABEL, isCurrentMonth as isCurrentMonthCalling } from "@/lib/calling-tasks";
import { todayIstIso } from "@/lib/tz";
import { startOfWeek, subMonths } from "date-fns";
import { CheckCircle2, Flame, ImageIcon, Clock3, AlertTriangle } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";

export const Route = createFileRoute("/_app/dashboard")({ component: Dashboard });

function Dashboard() {
  const { profile } = useAuth();
  if (!profile) return null;
  if (profile.department === "admin") return <AdminDashboard />;
  if (profile.department === "hr") return <HrDashboard />;
  if (profile.department === "editor") return <EditorDashboard />;
  if (profile.department === "graphic_designer") return <DesignerDashboard />;
  if (profile.department === "ai") return <AiDashboard />;
  if (profile.department === "scriptwriter") return <ScriptwriterDashboard />;
  if (profile.department === "marketer") return <MarketerDashboard />;
  if (profile.department === "seo") return <SeoDashboard />;
  if (profile.department === "smm") return <SmmDashboard />;
  if (profile.department === "calling") return <CallingDashboard />;
  return <GenericDashboard />;
}

function CallingDashboard() {
  const { profile } = useAuth();
  const { data: subs = [] } = useMyCallingSubmissions();
  useMyCallingOtherTasks();
  const [open, setOpen] = useState(false);
  if (!profile) return null;

  const today = todayIstIso();
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const daysElapsed = Math.max(1, now.getDate());

  const fmtINR = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;

  const todayRows = subs.filter((s) => s.submission_date === today);
  const weekRows = subs.filter(
    (s) => parseISO(s.submission_date) >= weekStart && parseISO(s.submission_date) <= now,
  );
  const monthRows = subs.filter((s) => parseISO(s.submission_date) >= monthStart);

  const revenueToday = todayRows.reduce((s, r) => s + Number(r.revenue_recovered ?? 0), 0);
  const cartsRecoveredToday = todayRows.filter(
    (r) => r.calling_type === "abandoned_cart" && Number(r.successfully_converted ?? 0) > 0,
  ).length;
  const abandonedToday = todayRows.filter((r) => r.calling_type === "abandoned_cart");
  const totalAbToday = abandonedToday.reduce((s, r) => s + Number(r.total_abandoned_calls ?? 0), 0);
  const convertedToday = abandonedToday.reduce((s, r) => s + Number(r.successfully_converted ?? 0), 0);
  const convRateToday = totalAbToday ? Math.round((convertedToday / totalAbToday) * 100) : null;

  // Calls by type today
  const byType = new Map<string, number>();
  for (const r of todayRows) {
    byType.set(r.calling_type, (byType.get(r.calling_type) ?? 0) + 1);
  }
  const typeRows = Array.from(byType.entries())
    .map(([t, c]) => ({ type: t, label: CALLING_TYPE_LABEL[t as keyof typeof CALLING_TYPE_LABEL] ?? t, count: c }))
    .sort((a, b) => b.count - a.count);

  // Best recovery day this month
  const dayRevenue = new Map<string, number>();
  for (const r of monthRows) {
    const rev = Number(r.revenue_recovered ?? 0);
    if (rev > 0) dayRevenue.set(r.submission_date, (dayRevenue.get(r.submission_date) ?? 0) + rev);
  }
  let bestDay: { date: string; total: number } | null = null;
  for (const [d, t] of dayRevenue) {
    if (!bestDay || t > bestDay.total) bestDay = { date: d, total: t };
  }
  const revenueMonth = monthRows.reduce((s, r) => s + Number(r.revenue_recovered ?? 0), 0);
  const monthlyAvg = revenueMonth / daysElapsed;

  return (
    <>
      <AppHeader
        subtitle={`Calling · ${todayRows.length} calls today`}
        ctaLabel="+ New Entry"
        onCta={() => setOpen(true)}
      />
      <div className="flex w-full flex-1 flex-col gap-6 px-6 pb-10 pt-2">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <DarkKpi label="Revenue Today" value={fmtINR(revenueToday)} sub="recovered today" />
          <Kpi label="Calls Today" value={todayRows.length} sub="logged today" tone="green" />
          <Kpi
            label="Carts Recovered Today"
            value={cartsRecoveredToday}
            sub={`${convertedToday} carts converted`}
            tone="green"
          />
          <Kpi
            label="Conversion Rate Today"
            value={convRateToday === null ? "—" : `${convRateToday}%`}
            sub={totalAbToday ? `${convertedToday}/${totalAbToday} carts` : "no abandoned calls"}
            tone="green"
          />
          <Kpi label="Calls This Week" value={weekRows.length} sub="Mon–today" tone="blue" />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <SectionHeader tiny="Calls by Type Today" heading="Today's breakdown" />
            <div className="mt-4 flex flex-col gap-2">
              {typeRows.length === 0 && (
                <p className="text-[12px] text-muted-foreground">No calls logged today</p>
              )}
              {typeRows.map((r) => (
                <div key={r.type} className="flex items-center justify-between border-b border-border pb-2 last:border-b-0 last:pb-0">
                  <span className="text-[12px] font-medium text-[#18181B]">{r.label}</span>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">{r.count}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <SectionHeader tiny="Best Recovery Day" heading="This month" />
            {!bestDay ? (
              <p className="mt-6 text-[12px] text-muted-foreground">No recoveries yet</p>
            ) : (
              <div className="mt-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                  Best Day: {format(parseISO(bestDay.date), "EEE, dd MMM")}
                </div>
                <div className="mt-2 text-[32px] font-bold leading-none text-[#18181B]">
                  {fmtINR(bestDay.total)}
                </div>
                <div className="mt-1 text-[12px] text-[#16A34A]">recovered</div>
                <div className="mt-5 text-[12px] text-muted-foreground">
                  Monthly Average: <span className="font-semibold text-[#18181B]">{fmtINR(monthlyAvg)}/day</span>
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Tile icon={<CheckCircle2 className="h-5 w-5" />} tone="green" value={fmtINR(revenueMonth)} label="Revenue this month" />
          <Tile icon={<Clock3 className="h-5 w-5" />} tone="blue" value={weekRows.length} label="Total calls this week" />
        </div>
      </div>
      <CallingTaskUpdateModal open={open} onOpenChange={setOpen} />
    </>
  );
}

function AdminDashboard() {
  return <AdminDashboardView />;
}


function SmmDashboard() {
  const { profile } = useAuth();
  const { data: tasks = [] } = useMySmmTasks();
  useMySmmOtherTasks();
  const [open, setOpen] = useState(false);
  if (!profile) return null;

  const today = todayIstIso();
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  // Sunday skip logic for "yesterday"
  const dow = now.getDay(); // 0 Sun, 1 Mon
  const yDate = new Date(now);
  if (dow === 1 || dow === 0) {
    // Mon or Sun => last Friday
    const back = dow === 1 ? 3 : 2;
    yDate.setDate(now.getDate() - back);
  } else {
    yDate.setDate(now.getDate() - 1);
  }
  const yesterday = format(yDate, "yyyy-MM-dd");

  const taskDuration = (t: { category_data: Record<string, unknown> }) => {
    const cd = t.category_data ?? {};
    const explicit = Number(cd.duration_minutes ?? 0);
    if (explicit > 0) return explicit;
    const s = cd.start_time as string | undefined;
    const e = cd.end_time as string | undefined;
    if (s && e) {
      const [sh, sm] = s.split(":").map(Number);
      const [eh, em] = e.split(":").map(Number);
      return Math.max(0, eh * 60 + em - (sh * 60 + sm));
    }
    return 0;
  };
  const fmtH = (mins: number) => {
    if (!mins) return "0h";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
  };

  const todayTasks = tasks.filter((t) => t.task_date === today);
  const yesterdayTasks = tasks.filter((t) => t.task_date === yesterday);
  const minsToday = todayTasks.reduce((s, t) => s + taskDuration(t), 0);
  const pendingTasks = tasks.filter((t) => t.status === "work_in_progress").length;
  const monthTasks = tasks.filter((t) => parseISO(t.task_date) >= monthStart);
  const minsMonth = monthTasks.reduce((s, t) => s + taskDuration(t), 0);
  const dayDelta = todayTasks.length - yesterdayTasks.length;

  const weekTasks = tasks.filter(
    (t) => parseISO(t.task_date) >= weekStart && parseISO(t.task_date) <= now,
  );
  const minsWeek = weekTasks.reduce((s, t) => s + taskDuration(t), 0);

  // Pie data
  const catMap = new Map<string, number>();
  for (const t of weekTasks) {
    catMap.set(t.task_category, (catMap.get(t.task_category) ?? 0) + taskDuration(t));
  }
  const pieData = Array.from(catMap.entries())
    .filter(([, m]) => m > 0)
    .map(([cat, mins]) => ({
      category: cat,
      label: SMM_CATEGORY_LABEL[cat as keyof typeof SMM_CATEGORY_LABEL] ?? cat,
      mins,
    }))
    .sort((a, b) => b.mins - a.mins);

  const PIE_COLORS = [
    "#2563EB", "#16A34A", "#D97706", "#DC2626", "#7C3AED",
    "#0EA5E9", "#F59E0B", "#10B981", "#6B7280",
  ];

  // Daily completion rate Mon..today (skip Sunday)
  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dailyRows: { label: string; total: number; completed: number }[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    if (d > now) break;
    if (d.getDay() === 0) continue; // skip Sunday
    const iso = format(d, "yyyy-MM-dd");
    const rows = tasks.filter((t) => t.task_date === iso);
    dailyRows.push({
      label: dayLabels[d.getDay()],
      total: rows.length,
      completed: rows.filter((t) => t.status === "completed").length,
    });
  }

  const weekCompleted = weekTasks.filter((t) => t.status === "completed").length;
  const weekCompletionRate = weekTasks.length
    ? Math.round((weekCompleted / weekTasks.length) * 100)
    : 0;

  return (
    <>
      <AppHeader
        subtitle={`SMM · ${todayTasks.length} tasks today`}
        ctaLabel="+ New Entry"
        onCta={() => setOpen(true)}
      />
      <div className="flex w-full flex-1 flex-col gap-6 px-6 pb-10 pt-2">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <DarkKpi label="Hours Today" value={fmtH(minsToday)} sub="logged today" />
          <Kpi label="Tasks Today" value={todayTasks.length} sub="logged today" tone="green" />
          <Kpi
            label="Tasks Yesterday"
            value={yesterdayTasks.length}
            sub={
              dayDelta === 0
                ? "same as today"
                : dayDelta > 0
                  ? `+${dayDelta} vs today`
                  : `${dayDelta} vs today`
            }
            tone={dayDelta >= 0 ? "green" : "amber"}
          />
          <Kpi
            label="Pending Tasks"
            value={pendingTasks}
            sub={pendingTasks ? "in progress" : "all clear"}
            tone={pendingTasks ? "amber" : "green"}
            dot={pendingTasks > 0}
          />
          <Kpi label="Hours This Month" value={fmtH(minsMonth)} sub="logged this month" tone="green" />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <SectionHeader tiny="Time Distribution" heading="Time spent this week by category" />
            {pieData.length === 0 ? (
              <p className="mt-6 text-[12px] text-muted-foreground">No tasks logged this week.</p>
            ) : (
              <div className="mt-4 h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="mins" nameKey="label" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip formatter={(value: number, _n, p) => [fmtH(value), p?.payload?.label]} />
                    <Legend
                      wrapperStyle={{ fontSize: 11 }}
                      formatter={(v: string, entry) => {
                        const item = entry?.payload as unknown as { payload?: { mins: number } };
                        return `${v} · ${fmtH(item?.payload?.mins ?? 0)}`;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
          <Card>
            <SectionHeader tiny="Daily Completion Rate This Week" heading="Completion by day" />
            <div className="mt-4 flex flex-col gap-2">
              {dailyRows.map((d) => (
                <div key={d.label} className="flex items-center justify-between border-b border-border pb-2 last:border-b-0 last:pb-0">
                  <span className="text-[12px] font-medium text-[#18181B]">{d.label}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {d.total === 0
                      ? "No tasks"
                      : `${d.completed}/${d.total} · ${Math.round((d.completed / d.total) * 100)}%`}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Tile icon={<Clock3 className="h-5 w-5" />} tone="blue" value={fmtH(minsWeek)} label="Total hours this week" />
          <Tile icon={<CheckCircle2 className="h-5 w-5" />} tone="green" value={`${weekCompletionRate}%`} label="Weekly completion rate" />
        </div>
      </div>
      <SmmTaskUpdateModal open={open} onOpenChange={setOpen} />
    </>
  );
}

function MarketerDashboard() {
  const { profile } = useAuth();
  const { data: tasks = [] } = useMyMarketerTasks();
  useMyMarketerOtherTasks();
  const [open, setOpen] = useState(false);
  if (!profile) return null;

  const today = todayIstIso();
  const now = new Date();
  const yesterday = format(new Date(now.getTime() - 86400000), "yyyy-MM-dd");
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const lastWeekStart = new Date(weekStart.getTime() - 7 * 86400000);
  const lastWeekEnd = new Date(weekStart.getTime() - 1);

  const taskDuration = (t: { category_data: Record<string, unknown> }) => {
    const cd = t.category_data ?? {};
    const explicit = Number(cd.duration_minutes ?? 0);
    if (explicit > 0) return explicit;
    const start = cd.start_time as string | undefined;
    const end = cd.end_time as string | undefined;
    if (start && end) {
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      return Math.max(0, eh * 60 + em - (sh * 60 + sm));
    }
    return 0;
  };

  const fmtH = (mins: number) => {
    if (!mins) return "0h";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
  };

  const todayTasks = tasks.filter((t) => t.task_date === today);
  const yesterdayTasks = tasks.filter((t) => t.task_date === yesterday);
  const minsToday = todayTasks.reduce((s, t) => s + taskDuration(t), 0);
  const tasksCompletedToday = todayTasks.filter((t) => t.status === "completed").length;
  const tasksCompletedYesterday = yesterdayTasks.filter((t) => t.status === "completed").length;
  const dayDelta = tasksCompletedToday - tasksCompletedYesterday;
  const pendingTasks = tasks.filter((t) => t.status === "work_in_progress").length;

  const weekTasks = tasks.filter(
    (t) => parseISO(t.task_date) >= weekStart && parseISO(t.task_date) <= now,
  );
  const lastWeekTasks = tasks.filter((t) => {
    const d = parseISO(t.task_date);
    return d >= lastWeekStart && d <= lastWeekEnd;
  });
  const minsThisWeek = weekTasks.reduce((s, t) => s + taskDuration(t), 0);
  const minsLastWeek = lastWeekTasks.reduce((s, t) => s + taskDuration(t), 0);
  const weekDelta = minsThisWeek - minsLastWeek;

  // Category breakdown (this week)
  const catTotals = new Map<string, { mins: number; count: number }>();
  for (const t of weekTasks) {
    const cur = catTotals.get(t.task_category) ?? { mins: 0, count: 0 };
    cur.mins += taskDuration(t);
    cur.count += 1;
    catTotals.set(t.task_category, cur);
  }
  const breakdown = Array.from(catTotals.entries())
    .map(([cat, v]) => ({
      category: cat as keyof typeof MARKETER_CATEGORY_LABEL,
      label: MARKETER_CATEGORY_LABEL[cat as keyof typeof MARKETER_CATEGORY_LABEL] ?? cat,
      mins: v.mins,
      count: v.count,
    }))
    .filter((r) => r.mins > 0 || r.count > 0)
    .sort((a, b) => b.mins - a.mins);

  const PIE_COLORS = [
    "#2563EB", "#16A34A", "#D97706", "#DC2626", "#7C3AED",
    "#0EA5E9", "#F59E0B", "#10B981", "#6B7280",
  ];

  // Section 3 metrics
  const monthRows = tasks.filter((t) => isCurrentMonthMarketer(t.task_date));
  const adsTracked = monthRows.filter(
    (t) => t.task_category === "ad_campaign_tracking" && t.status === "completed",
  ).length;
  const competitorResearch = monthRows.filter(
    (t) => t.task_category === "competitor_research",
  ).length;
  const productResearch = monthRows.filter(
    (t) => t.task_category === "product_research",
  ).length;
  const meetingMinsWeek = weekTasks
    .filter((t) => t.task_category === "meeting")
    .reduce((s, t) => s + taskDuration(t), 0);

  return (
    <>
      <AppHeader
        subtitle={`Marketing · ${todayTasks.length} tasks today`}
        ctaLabel="+ New Entry"
        onCta={() => setOpen(true)}
      />
      <div className="flex w-full flex-1 flex-col gap-6 px-6 pb-10 pt-2">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <DarkKpi label="Hours Today" value={fmtH(minsToday)} sub="logged today" />
          <Kpi
            label="Tasks Completed Today"
            value={tasksCompletedToday}
            sub="marked done"
            tone="green"
          />
          <Kpi
            label="Tasks Yesterday"
            value={tasksCompletedYesterday}
            sub={
              dayDelta === 0
                ? "same as today"
                : dayDelta > 0
                  ? `+${dayDelta} vs today`
                  : `${dayDelta} vs today`
            }
            tone={dayDelta <= 0 ? "green" : "amber"}
          />
          <Kpi
            label="Pending Tasks"
            value={pendingTasks}
            sub={pendingTasks ? "in progress" : "all clear"}
            tone={pendingTasks ? "amber" : "green"}
            dot={pendingTasks > 0}
          />
          <Kpi
            label="Hours This Week"
            value={fmtH(minsThisWeek)}
            sub={
              weekDelta === 0
                ? `vs last week: ${fmtH(minsLastWeek)}`
                : weekDelta > 0
                  ? `+${fmtH(weekDelta)} vs last week`
                  : `${fmtH(weekDelta)} vs last week`
            }
            tone={weekDelta >= 0 ? "green" : "amber"}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <SectionHeader
              tiny="Time Distribution"
              heading="Time spent this week by category"
            />
            {breakdown.length === 0 ? (
              <p className="mt-6 text-[12px] text-muted-foreground">
                No tasks logged this week.
              </p>
            ) : (
              <div className="mt-4 h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={breakdown}
                      dataKey="mins"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={50}
                      paddingAngle={2}
                    >
                      {breakdown.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip
                      formatter={(value: number, _name, payload) => [
                        fmtH(value),
                        payload?.payload?.label,
                      ]}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11 }}
                      formatter={(v: string, entry) => {
                        const item = entry?.payload as unknown as {
                          payload?: { mins: number };
                        };
                        return `${v} · ${fmtH(item?.payload?.mins ?? 0)}`;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
          <Card>
            <SectionHeader tiny="Breakdown" heading="Category leaderboard" />
            <div className="mt-4 flex flex-col gap-3">
              {breakdown.length === 0 && (
                <p className="text-[12px] text-muted-foreground">
                  No tasks logged this week
                </p>
              )}
              {breakdown.map((r, i) => (
                <div
                  key={r.category}
                  className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-b-0 last:pb-0"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    <span className="line-clamp-1 text-[12px] font-medium text-[#18181B]">
                      {r.label}
                    </span>
                  </div>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {fmtH(r.mins)} · {r.count} task{r.count === 1 ? "" : "s"}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Tile
            icon={<ImageIcon className="h-5 w-5" />}
            tone="blue"
            value={adsTracked}
            label="Ad campaigns tracked"
          />
          <Tile
            icon={<ImageIcon className="h-5 w-5" />}
            tone="blue"
            value={competitorResearch}
            label="Competitor research"
          />
          <Tile
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="green"
            value={productResearch}
            label="Product research"
          />
          <Tile
            icon={<Clock3 className="h-5 w-5" />}
            tone="amber"
            value={fmtH(meetingMinsWeek)}
            label="Meeting time this week"
          />
        </div>
      </div>
      <MarketerTaskUpdateModal open={open} onOpenChange={setOpen} />
    </>
  );
}

function SeoDashboard() {
  const { profile } = useAuth();
  const { data: tasks = [] } = useMySeoTasks();
  useMySeoOtherTasks();
  const [open, setOpen] = useState(false);
  if (!profile) return null;

  const today = todayIstIso();
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const lastWeekStart = new Date(weekStart.getTime() - 7 * 86400000);
  const lastWeekEnd = new Date(weekStart.getTime() - 1);

  const dur = (t: { start_time: string; end_time: string }) => {
    if (!t.start_time || !t.end_time) return 0;
    const [sh, sm] = t.start_time.split(":").map(Number);
    const [eh, em] = t.end_time.split(":").map(Number);
    return Math.max(0, eh * 60 + em - (sh * 60 + sm));
  };
  const fmtH = (mins: number) => {
    if (!mins) return "0h";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
  };

  const todayTasks = tasks.filter((t) => t.task_date === today);
  const minsToday = todayTasks.reduce((s, t) => s + dur(t), 0);
  const pendingTasks = tasks.filter((t) => t.status === "work_in_progress").length;
  const monthRows = tasks.filter((t) => isCurrentMonthSeo(t.task_date));
  const blogsPublished = monthRows.filter((t) => t.task_category === "blog_published").length;
  const backlinksCreated = monthRows.filter((t) => t.task_category === "backlinks_created").length;

  const weekTasks = tasks.filter(
    (t) => parseISO(t.task_date) >= weekStart && parseISO(t.task_date) <= now,
  );
  const lastWeekTasks = tasks.filter((t) => {
    const d = parseISO(t.task_date);
    return d >= lastWeekStart && d <= lastWeekEnd;
  });
  const minsWeek = weekTasks.reduce((s, t) => s + dur(t), 0);
  const minsLastWeek = lastWeekTasks.reduce((s, t) => s + dur(t), 0);
  const weekDelta = minsWeek - minsLastWeek;

  // Bar chart: time by category this week
  const catMap = new Map<string, number>();
  for (const t of weekTasks) {
    catMap.set(t.task_category, (catMap.get(t.task_category) ?? 0) + dur(t));
  }
  const barData = Array.from(catMap.entries())
    .filter(([, m]) => m > 0)
    .map(([cat, mins]) => ({
      label: SEO_CATEGORY_LABEL[cat as keyof typeof SEO_CATEGORY_LABEL] ?? cat,
      hours: +(mins / 60).toFixed(2),
      mins,
    }))
    .sort((a, b) => b.mins - a.mins)
    .slice(0, 8);

  // Today by category
  const todayCatMap = new Map<string, { count: number; mins: number }>();
  for (const t of todayTasks) {
    const cur = todayCatMap.get(t.task_category) ?? { count: 0, mins: 0 };
    cur.count += 1;
    cur.mins += dur(t);
    todayCatMap.set(t.task_category, cur);
  }
  const todayCatRows = Array.from(todayCatMap.entries())
    .map(([cat, v]) => ({
      label: SEO_CATEGORY_LABEL[cat as keyof typeof SEO_CATEGORY_LABEL] ?? cat,
      count: v.count,
      mins: v.mins,
    }))
    .sort((a, b) => b.mins - a.mins);

  // Tiles
  const gscThisWeek = weekTasks.filter((t) => t.task_category === "gsc_check").length;
  const competitorMonth = monthRows.filter((t) => t.task_category === "competitor_analysis").length;

  return (
    <>
      <AppHeader
        subtitle={`SEO · ${todayTasks.length} tasks today`}
        ctaLabel="+ New Entry"
        onCta={() => setOpen(true)}
      />
      <div className="flex w-full flex-1 flex-col gap-6 px-6 pb-10 pt-2">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <DarkKpi label="Hours Today" value={fmtH(minsToday)} sub="logged today" />
          <Kpi label="Tasks Today" value={todayTasks.length} sub="logged today" tone="green" />
          <Kpi
            label="Pending Tasks"
            value={pendingTasks}
            sub={pendingTasks ? "in progress" : "all clear"}
            tone={pendingTasks ? "amber" : "green"}
            dot={pendingTasks > 0}
          />
          <Kpi label="Blogs Published" value={blogsPublished} sub="this month" tone="blue" />
          <Kpi label="Backlinks Created" value={backlinksCreated} sub="this month" tone="blue" />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <SectionHeader tiny="Time by Category" heading="This week (hours)" />
            {barData.length === 0 ? (
              <p className="mt-6 text-[12px] text-muted-foreground">No tasks logged this week.</p>
            ) : (
              <div className="mt-4" style={{ height: Math.max(220, barData.length * 36) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={130} />
                    <RTooltip formatter={(v: number) => [`${v}h`, "Time"]} />
                    <Bar dataKey="hours" fill="#2563EB" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
          <Card>
            <SectionHeader tiny="Today's Tasks" heading="By category" />
            <div className="mt-4 flex flex-col gap-2">
              {todayCatRows.length === 0 && (
                <p className="text-[12px] text-muted-foreground">No tasks logged today</p>
              )}
              {todayCatRows.map((r) => (
                <div key={r.label} className="flex items-center justify-between border-b border-border pb-2 last:border-b-0 last:pb-0">
                  <span className="text-[12px] font-medium text-[#18181B]">{r.label}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {r.count} · {fmtH(r.mins)}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Tile icon={<CheckCircle2 className="h-5 w-5" />} tone="blue" value={gscThisWeek} label="GSC checks this week" />
          <Tile icon={<ImageIcon className="h-5 w-5" />} tone="blue" value={competitorMonth} label="Competitor analyses this month" />
          <Tile icon={<Clock3 className="h-5 w-5" />} tone="green" value={fmtH(minsWeek)} label="Hours this week" />
          <Tile
            icon={<Clock3 className="h-5 w-5" />}
            tone="amber"
            value={fmtH(minsLastWeek)}
            label="Hours last week"
            sub={
              weekDelta === 0
                ? "same as this week"
                : weekDelta > 0
                  ? `+${fmtH(weekDelta)} this week`
                  : `${fmtH(weekDelta)} this week`
            }
          />
        </div>
      </div>
      <SeoTaskUpdateModal open={open} onOpenChange={setOpen} />
    </>
  );
}

function ScriptwriterDashboard() {
  const { profile } = useAuth();
  const { data: scripts = [] } = useMyScriptwriterSubmissions();
  const { data: otherTasks = [] } = useMyScriptwriterOtherTasks();
  const [open, setOpen] = useState(false);

  if (!profile) return null;

  const today = todayIstIso();
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const prevMonth = subMonths(now, 1);

  const monthRows = scripts.filter((s) => isCurrentMonthScript(s.submission_date));
  const monthCount = monthRows.length;
  const todayCount = scripts.filter((s) => s.submission_date === today).length;
  const weekCount = scripts.filter(
    (s) => parseISO(s.submission_date) >= weekStart && parseISO(s.submission_date) <= now,
  ).length;
  const lastMonthCount = scripts.filter((s) => {
    const d = parseISO(s.submission_date);
    return d.getMonth() === prevMonth.getMonth() && d.getFullYear() === prevMonth.getFullYear();
  }).length;
  const monthDelta = monthCount - lastMonthCount;

  // Total work-time this month from other_tasks (the only source with start/end)
  const monthOtherTasks = otherTasks.filter((t) => isCurrentMonthScript(t.task_date));
  const totalMonthMinutes = monthOtherTasks.reduce((sum, t) => {
    const [sh, sm] = t.start_time.split(":").map(Number);
    const [eh, em] = t.end_time.split(":").map(Number);
    return sum + Math.max(0, eh * 60 + em - (sh * 60 + sm));
  }, 0);
  const avgMinsPerScript = monthCount > 0 && totalMonthMinutes > 0
    ? Math.round(totalMonthMinutes / monthCount)
    : 0;

  const fmtDuration = (mins: number) => {
    if (!mins) return "0m";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
  };

  // Bulk batches: group by created_at truncated to the second (bulk RPC writes all rows together)
  const batchMap = new Map<string, typeof scripts>();
  for (const s of scripts) {
    const key = `${s.submission_date}|${s.created_at.slice(0, 19)}`;
    if (!batchMap.has(key)) batchMap.set(key, []);
    batchMap.get(key)!.push(s);
  }
  const batches = Array.from(batchMap.entries())
    .map(([key, rows]) => ({
      key,
      date: rows[0].submission_date,
      created_at: rows[0].created_at,
      count: rows.length,
      product: rows[0].product_name,
    }))
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, 5);

  // Week breakdown: Mon → today (IST)
  const weekDays: { label: string; iso: string; count: number; isFuture: boolean }[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    const iso = format(d, "yyyy-MM-dd");
    const isFuture = d > now;
    const count = scripts.filter((s) => s.submission_date === iso).length;
    weekDays.push({ label: format(d, "EEE"), iso, count, isFuture });
  }

  return (
    <>
      <AppHeader
        subtitle={`Scriptwriter · ${monthCount} scripts this month`}
        ctaLabel="+ New Entry"
        onCta={() => setOpen(true)}
      />
      <div className="flex w-full flex-1 flex-col gap-6 px-6 pb-10 pt-2">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <DarkKpi label="Scripts This Month" value={monthCount} sub="this month" />
          <Kpi label="Scripts Today" value={todayCount} sub="logged today" tone="green" />
          <Kpi label="Scripts This Week" value={weekCount} sub="since Monday" tone="green" />
          <Kpi
            label="Avg Time Per Script"
            value={avgMinsPerScript ? `${avgMinsPerScript} mins` : "—"}
            sub={avgMinsPerScript ? "from logged sessions" : "no time data"}
            tone="green"
          />
          <Kpi
            label="Last Month"
            value={lastMonthCount}
            sub={
              monthDelta === 0
                ? "same as last month"
                : monthDelta > 0
                  ? `+${monthDelta} vs last month`
                  : `${monthDelta} vs last month`
            }
            tone={monthDelta >= 0 ? "green" : "amber"}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          <Card>
            <SectionHeader tiny="Recent Bulk Batches" heading="Latest bulk submissions" />
            <div className="mt-4 flex flex-col gap-3">
              {batches.length === 0 && (
                <p className="text-[12px] text-muted-foreground">No bulk submissions yet</p>
              )}
              {batches.map((b) => (
                <div
                  key={b.key}
                  className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-b-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <div className="line-clamp-1 text-[12px] font-medium text-[#18181B]">
                      {b.product}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {format(parseISO(b.date), "EEE, dd MMM")}
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[10px] font-semibold text-[#2563EB]">
                    {b.count} scripts
                  </span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <SectionHeader tiny="This Week Breakdown" heading="Scripts by day" />
            <div className="mt-4 flex flex-col gap-2">
              {weekDays
                .filter((d) => !d.isFuture)
                .map((d) => (
                  <div
                    key={d.iso}
                    className="flex items-center justify-between text-[12px]"
                  >
                    <span className="font-medium text-[#18181B]">{d.label}</span>
                    <span className="text-muted-foreground">
                      {d.count > 0 ? `${d.count} script${d.count === 1 ? "" : "s"}` : "—"}
                    </span>
                  </div>
                ))}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Tile
            icon={<ImageIcon className="h-5 w-5" />}
            tone="blue"
            value={monthCount}
            label={
              monthDelta === 0
                ? "Monthly scripts (same as last month)"
                : monthDelta > 0
                  ? `Monthly scripts (+${monthDelta} vs last month)`
                  : `Monthly scripts (${monthDelta} vs last month)`
            }
          />
          <Tile
            icon={<Clock3 className="h-5 w-5" />}
            tone="green"
            value={fmtDuration(totalMonthMinutes)}
            label="Total time this month"
          />
        </div>
      </div>
      <ScriptwriterTaskUpdateModal open={open} onOpenChange={setOpen} />
    </>
  );
}

function DesignerDashboard() {
  const { profile } = useAuth();
  const { data: creatives = [], isLoading } = useMyDesignerSubmissions();
  useMyDesignerOtherTasks();
  const [open, setOpen] = useState(false);

  if (!profile) return null;

  const today = todayIstIso();
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const prevMonth = subMonths(now, 1);

  const monthRows = creatives.filter((c) => isCurrentMonthDesigner(c.submission_date));
  const monthCount = monthRows.length;
  const todayCount = creatives.filter((c) => c.submission_date === today).length;
  const weekCount = creatives.filter(
    (c) => parseISO(c.submission_date) >= weekStart && parseISO(c.submission_date) <= now,
  ).length;
  const pendingCorrections = creatives.filter((c) => c.correction_status === "fix").length;
  const lastMonthCount = creatives.filter((c) => {
    const d = parseISO(c.submission_date);
    return d.getMonth() === prevMonth.getMonth() && d.getFullYear() === prevMonth.getFullYear();
  }).length;
  const monthDelta = monthCount - lastMonthCount;

  // Working-day average (days with at least 1 entry this month)
  const workingDays = new Set(
    monthRows.map((r) => r.submission_date),
  ).size;
  const avgPerDay = workingDays > 0 ? monthCount / workingDays : 0;
  const avgMinsPerDesign = avgPerDay > 0 ? Math.round((7 * 60) / avgPerDay) : 0;
  const avgH = Math.floor(avgMinsPerDesign / 60);
  const avgM = avgMinsPerDesign % 60;

  const pendingList = creatives
    .filter((c) => c.correction_status === "fix")
    .sort((a, b) => b.submission_date.localeCompare(a.submission_date))
    .slice(0, 5);

  const completed = monthRows.filter((r) => r.status === "completed").length;
  const waiting = monthRows.filter((r) => r.status === "waiting_for_approval").length;

  return (
    <>
      <AppHeader
        subtitle={`Graphic Designer · ${monthCount} designs logged`}
        ctaLabel="+ New Entry"
        onCta={() => setOpen(true)}
      />
      <div className="flex w-full flex-1 flex-col gap-6 px-6 pb-10 pt-2">
        {/* KPI ROW */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <DarkKpi label="Designs This Month" value={monthCount} sub="this month" />
          <Kpi label="Designs Today" value={todayCount} sub="logged today" tone="green" />
          <Kpi label="Designs This Week" value={weekCount} sub="since Monday" tone="green" />
          <Kpi
            label="Pending Corrections"
            value={pendingCorrections}
            sub={pendingCorrections ? "needs fixes" : "all clear"}
            tone={pendingCorrections ? "amber" : "green"}
            dot={pendingCorrections > 0}
          />
          <Kpi
            label="Last Month"
            value={lastMonthCount}
            sub={
              monthDelta === 0
                ? "same as last month"
                : monthDelta > 0
                  ? `+${monthDelta} vs last month`
                  : `${monthDelta} vs last month`
            }
            tone={monthDelta >= 0 ? "green" : "amber"}
          />
        </div>

        {/* SECTION 2 */}
        <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          <Card>
            <SectionHeader tiny="Throughput" heading="Average turnaround time" />
            <div className="mt-6">
              {workingDays === 0 || avgMinsPerDesign === 0 ? (
                <p className="text-[14px] text-muted-foreground">No data yet</p>
              ) : (
                <>
                  <div className="text-[36px] font-bold leading-none text-[#18181B]">
                    {avgH > 0 ? `${avgH} hrs ` : ""}
                    {avgM} mins
                    <span className="ml-2 text-[14px] font-medium text-muted-foreground">
                      per design
                    </span>
                  </div>
                  <div className="mt-3 text-[12px] text-muted-foreground">
                    Based on {workingDays} working day{workingDays === 1 ? "" : "s"} · {monthCount}{" "}
                    design{monthCount === 1 ? "" : "s"} total
                  </div>
                </>
              )}
            </div>
          </Card>
          <Card>
            <SectionHeader tiny="Needs Attention" heading="Pending corrections" />
            <div className="mt-4 flex flex-col gap-3">
              {isLoading && (
                <p className="text-[12px] text-muted-foreground">Loading…</p>
              )}
              {!isLoading && pendingList.length === 0 && (
                <div className="flex items-center gap-2 text-[12px] text-[#16A34A]">
                  <CheckCircle2 className="h-4 w-4" /> No pending corrections 🎉
                </div>
              )}
              {pendingList.map((c) => (
                <div
                  key={c.id}
                  className="border-b border-border pb-2 last:border-b-0 last:pb-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="line-clamp-1 text-[12px] font-medium">
                      {c.creative_description.slice(0, 60)}
                    </div>
                    <span className="shrink-0 text-[10px] text-muted-foreground">
                      {format(parseISO(c.submission_date), "dd MMM")}
                    </span>
                  </div>
                  {c.correction_note && (
                    <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">
                      {c.correction_note.slice(0, 80)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* SECTION 3 */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Tile
            icon={<ImageIcon className="h-5 w-5" />}
            tone="blue"
            value={monthCount}
            label="Total designs this month"
          />
          <Tile
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="green"
            value={completed}
            label="Completed designs"
          />
          <Tile
            icon={<Clock3 className="h-5 w-5" />}
            tone="amber"
            value={waiting}
            label="Waiting approval"
          />
        </div>
      </div>
      <DesignerTaskUpdateModal open={open} onOpenChange={setOpen} />
    </>
  );
}

function AiDashboard() {
  const { profile } = useAuth();
  const { data: creatives = [], isLoading } = useMyAiSubmissions();
  useMyAiOtherTasks();
  const [open, setOpen] = useState(false);

  if (!profile) return null;

  const today = todayIstIso();
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const prevMonth = subMonths(now, 1);

  const monthRows = creatives.filter((c) => isCurrentMonthDesigner(c.submission_date));
  const monthCount = monthRows.length;
  const todayCount = creatives.filter((c) => c.submission_date === today).length;
  const weekCount = creatives.filter(
    (c) => parseISO(c.submission_date) >= weekStart && parseISO(c.submission_date) <= now,
  ).length;
  const pendingCorrections = creatives.filter((c) => c.correction_status === "fix").length;
  const lastMonthCount = creatives.filter((c) => {
    const d = parseISO(c.submission_date);
    return d.getMonth() === prevMonth.getMonth() && d.getFullYear() === prevMonth.getFullYear();
  }).length;
  const monthDelta = monthCount - lastMonthCount;

  // Working-day average (days with at least 1 entry this month)
  const workingDays = new Set(
    monthRows.map((r) => r.submission_date),
  ).size;
  const avgPerDay = workingDays > 0 ? monthCount / workingDays : 0;
  const avgMinsPerDesign = avgPerDay > 0 ? Math.round((7 * 60) / avgPerDay) : 0;
  const avgH = Math.floor(avgMinsPerDesign / 60);
  const avgM = avgMinsPerDesign % 60;

  const pendingList = creatives
    .filter((c) => c.correction_status === "fix")
    .sort((a, b) => b.submission_date.localeCompare(a.submission_date))
    .slice(0, 5);

  const completed = monthRows.filter((r) => r.status === "completed").length;
  const waiting = monthRows.filter((r) => r.status === "waiting_for_approval").length;

  return (
    <>
      <AppHeader
        subtitle={`AI · ${monthCount} creatives logged`}
        ctaLabel="+ New Entry"
        onCta={() => setOpen(true)}
      />
      <div className="flex w-full flex-1 flex-col gap-6 px-6 pb-10 pt-2">
        {/* KPI ROW */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <DarkKpi label="Designs This Month" value={monthCount} sub="this month" />
          <Kpi label="Designs Today" value={todayCount} sub="logged today" tone="green" />
          <Kpi label="Designs This Week" value={weekCount} sub="since Monday" tone="green" />
          <Kpi
            label="Pending Corrections"
            value={pendingCorrections}
            sub={pendingCorrections ? "needs fixes" : "all clear"}
            tone={pendingCorrections ? "amber" : "green"}
            dot={pendingCorrections > 0}
          />
          <Kpi
            label="Last Month"
            value={lastMonthCount}
            sub={
              monthDelta === 0
                ? "same as last month"
                : monthDelta > 0
                  ? `+${monthDelta} vs last month`
                  : `${monthDelta} vs last month`
            }
            tone={monthDelta >= 0 ? "green" : "amber"}
          />
        </div>

        {/* SECTION 2 */}
        <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          <Card>
            <SectionHeader tiny="Throughput" heading="Average turnaround time" />
            <div className="mt-6">
              {workingDays === 0 || avgMinsPerDesign === 0 ? (
                <p className="text-[14px] text-muted-foreground">No data yet</p>
              ) : (
                <>
                  <div className="text-[36px] font-bold leading-none text-[#18181B]">
                    {avgH > 0 ? `${avgH} hrs ` : ""}
                    {avgM} mins
                    <span className="ml-2 text-[14px] font-medium text-muted-foreground">
                      per design
                    </span>
                  </div>
                  <div className="mt-3 text-[12px] text-muted-foreground">
                    Based on {workingDays} working day{workingDays === 1 ? "" : "s"} · {monthCount}{" "}
                    design{monthCount === 1 ? "" : "s"} total
                  </div>
                </>
              )}
            </div>
          </Card>
          <Card>
            <SectionHeader tiny="Needs Attention" heading="Pending corrections" />
            <div className="mt-4 flex flex-col gap-3">
              {isLoading && (
                <p className="text-[12px] text-muted-foreground">Loading…</p>
              )}
              {!isLoading && pendingList.length === 0 && (
                <div className="flex items-center gap-2 text-[12px] text-[#16A34A]">
                  <CheckCircle2 className="h-4 w-4" /> No pending corrections 🎉
                </div>
              )}
              {pendingList.map((c) => (
                <div
                  key={c.id}
                  className="border-b border-border pb-2 last:border-b-0 last:pb-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="line-clamp-1 text-[12px] font-medium">
                      {c.creative_description.slice(0, 60)}
                    </div>
                    <span className="shrink-0 text-[10px] text-muted-foreground">
                      {format(parseISO(c.submission_date), "dd MMM")}
                    </span>
                  </div>
                  {c.correction_note && (
                    <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">
                      {c.correction_note.slice(0, 80)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* SECTION 3 */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Tile
            icon={<ImageIcon className="h-5 w-5" />}
            tone="blue"
            value={monthCount}
            label="Total designs this month"
          />
          <Tile
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="green"
            value={completed}
            label="Completed designs"
          />
          <Tile
            icon={<Clock3 className="h-5 w-5" />}
            tone="amber"
            value={waiting}
            label="Waiting approval"
          />
        </div>
      </div>
      <AiTaskUpdateModal open={open} onOpenChange={setOpen} />
    </>
  );
}

// ---------- Editor dashboard ----------

function EditorDashboard() {
  const { profile } = useAuth();
  const { data: videos = [], isLoading } = useMyVideoSubmissions();
  useMyOtherTasks(); // warm cache
  const [open, setOpen] = useState(false);

  if (!profile) return null;
  const dept = DEPARTMENT_LABELS[profile.department];
  // Only editors fall back to the legacy default of 80; other departments use the stored value as-is.
  const target = profile.monthly_target ?? (profile.department === "editor" ? 80 : 0);

  const monthVideos = videos.filter((v) => isCurrentMonth(v.submission_date));
  const completedThisMonth = monthVideos.filter((v) => v.status === "completed");
  const monthCount = completedThisMonth.length;
  const totalPoints = completedThisMonth.reduce((s, v) => s + v.points, 0);
  const corrections = monthVideos.filter((v) => v.correction_status === "fix").length;
  const reviewed = monthVideos.filter((v) => v.correction_status !== "pending").length;
  const approved = monthVideos.filter((v) => v.correction_status === "approved").length;
  const approvalRate = reviewed ? Math.round((approved / reviewed) * 100) : 0;
  const pct = Math.round((monthCount / target) * 100);

  // Adapt video rows to chart format
  const today = todayIstIso();
  const todayVideos = videos.filter((v) => v.submission_date === today).length;

  // --- New widget computations ---
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const prevMonth = subMonths(now, 1);
  const videosThisWeek = videos.filter(
    (v) => parseISO(v.submission_date) >= weekStart && parseISO(v.submission_date) <= now,
  ).length;
  const lastMonthCount = videos.filter((v) => {
    const d = parseISO(v.submission_date);
    return d.getMonth() === prevMonth.getMonth() && d.getFullYear() === prevMonth.getFullYear();
  }).length;
  const monthDelta = monthCount - lastMonthCount;

  // Streak: consecutive most-recent submission dates with no 'fix' rows
  const sortedDates = Array.from(new Set(videos.map((v) => v.submission_date))).sort((a, b) =>
    b.localeCompare(a),
  );
  let cleanStreak = 0;
  for (const d of sortedDates) {
    const dayRows = videos.filter((v) => v.submission_date === d);
    if (dayRows.some((r) => r.correction_status === "fix")) break;
    cleanStreak++;
  }

  const pointsPct = target > 0 ? Math.round((totalPoints / target) * 100) : 0;
  const progressColor =
    pointsPct > 80 ? "#16A34A" : pointsPct >= 50 ? "#D97706" : "#DC2626";

  const recentCorrectionNotes = videos
    .filter((v) => v.correction_status === "fix" && v.correction_note)
    .sort((a, b) => b.submission_date.localeCompare(a.submission_date))
    .slice(0, 3);

  return (
    <>
      <AppHeader
        subtitle={`${dept} · ${monthCount} tasks logged this month`}
        ctaLabel="+ New Entry"
        onCta={() => setOpen(true)}
      />
      <KpiRow
        items={[
          {
            label: "Videos This Month",
            value: String(monthCount),
            trend: "this month",
            variant: "primary",
          },
          {
            label: "Total Points",
            value: String(totalPoints),
            trend: `${todayVideos} today`,
          },
          {
            label: "Corrections",
            value: String(corrections),
            trend: `${corrections} pending fixes`,
            variant: "danger",
          },
          {
            label: "Approval Rate",
            value: reviewed ? `${approvalRate}%` : "—",
            trend: `${approved}/${reviewed} approved`,
          },
          { label: "Team Rank", value: "—", trend: "Coming soon" },
        ]}
      />
      <div className="grid gap-4 px-[22px] pb-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            Recent Videos
          </div>
          <h2 className="text-[18px] font-bold">Latest entries</h2>
          <div className="mt-4 flex flex-col gap-3">
            {isLoading && <p className="text-[12px] text-muted-foreground">Loading…</p>}
            {!isLoading && videos.length === 0 && (
              <p className="text-[12px] text-muted-foreground">
                No entries yet. Click + New Entry to add your first one.
              </p>
            )}
            {videos.slice(0, 6).map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-b-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="line-clamp-1 text-[12px] font-medium">
                    {v.creative_id} · {v.product_name}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {format(parseISO(v.submission_date), "EEE, dd MMM")} · {v.brand}
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium">
                  {VIDEO_STATUS_LABEL[v.status]}
                </span>
              </div>
            ))}
          </div>
        </div>
        <QuickSubmitCard onExpand={() => setOpen(true)} />
      </div>

      {/* New widgets — admin-style */}
      <div className="flex w-full flex-col gap-6 px-6 pb-10">
        <Card>
          <SectionHeader tiny="Monthly Goal" heading="Points vs target" />
          <div className="mt-4 flex items-end justify-between gap-3">
            <div className="text-[36px] font-bold leading-none text-[#18181B]">
              {totalPoints}
              <span className="ml-2 text-[14px] font-medium text-muted-foreground">
                / {target} points this month
              </span>
            </div>
            <div className="text-[14px] font-semibold" style={{ color: progressColor }}>
              {pointsPct}%
            </div>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(100, Math.max(0, pointsPct))}%`,
                background: progressColor,
              }}
            />
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Tile
            icon={<ImageIcon className="h-5 w-5" />}
            tone="blue"
            value={videosThisWeek}
            label="Videos this week"
          />
          <Tile
            icon={<Flame className="h-5 w-5" />}
            tone="green"
            value={cleanStreak}
            label={`Days clean${cleanStreak > 0 ? " 🔥" : ""}`}
          />
          <Tile
            icon={<ImageIcon className="h-5 w-5" />}
            tone="blue"
            value={monthCount}
            label={
              monthDelta === 0
                ? "Same as last month"
                : monthDelta > 0
                  ? `+${monthDelta} vs last month`
                  : `${monthDelta} vs last month`
            }
          />
          <Tile
            icon={<AlertTriangle className="h-5 w-5" />}
            tone={corrections > 0 ? "red" : "green"}
            value={corrections}
            label="Pending corrections"
          />
        </div>

        <Card>
          <SectionHeader tiny="Feedback" heading="Latest correction notes" />
          <div className="mt-4 flex flex-col gap-3">
            {recentCorrectionNotes.length === 0 && (
              <div className="flex items-center gap-2 text-[12px] text-[#16A34A]">
                <CheckCircle2 className="h-4 w-4" /> No corrections this month 🎉
              </div>
            )}
            {recentCorrectionNotes.map((v) => (
              <div
                key={v.id}
                className="border-b border-border pb-2 last:border-b-0 last:pb-0"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="line-clamp-1 text-[12px] font-medium text-[#18181B]">
                    {v.creative_id}
                  </div>
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {format(parseISO(v.submission_date), "dd MMM")}
                  </span>
                </div>
                <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">
                  {(v.correction_note ?? "").slice(0, 80)}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <TaskUpdateModal open={open} onOpenChange={setOpen} />
    </>
  );
}

// ---------- Generic dashboard for non-editors ----------

function GenericDashboard() {
  const { profile } = useAuth();
  const { data: rows = [], isLoading } = useMySubmissions();
  const [modalOpen, setModalOpen] = useState(false);

  if (!profile) return null;
  const config = getDepartmentConfig(profile.department);
  const dept = DEPARTMENT_LABELS[profile.department];
  const month = monthOutput(rows);
  const monthCount = month.length;
  const corrections = month.filter((r) => r.status === "correction_needed").length;
  const approved = month.filter((r) => r.status === "approved").length;
  const reviewed = month.filter((r) => r.status !== "pending").length;
  const approvalRate = reviewed ? Math.round((approved / reviewed) * 100) : 0;

  return (
    <>
      <AppHeader
        subtitle={`${dept} · ${monthCount} tasks logged this month`}
        ctaLabel={config.ctaLabel}
        onCta={() => setModalOpen(true)}
      />
      <KpiRow
        items={[
          {
            label: config.kpis.outputLabel,
            value: String(monthCount),
            trend: `${todayCount(rows)} today`,
            variant: "primary",
          },
          {
            label: "Corrections",
            value: String(corrections),
            trend: `${corrections} pending fixes`,
            variant: "danger",
          },
          {
            label: "Approval Rate",
            value: reviewed ? `${approvalRate}%` : "—",
            trend: `${approved}/${reviewed} approved`,
          },
          { label: "Team Rank", value: "—", trend: "Coming soon" },
        ]}
      />
      <div className="grid gap-4 px-[22px] pb-6 lg:grid-cols-[1.6fr_1fr]">
        <OutputTrendChart rows={rows} noun={config.itemNoun} />
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
            Recent Activity
          </div>
          <h2 className="text-[18px] font-bold">Latest entries</h2>
          <div className="mt-4 flex flex-col gap-3">
            {isLoading && <p className="text-[12px] text-muted-foreground">Loading…</p>}
            {!isLoading && rows.length === 0 && (
              <p className="text-[12px] text-muted-foreground">
                No entries yet. Click {config.ctaLabel} to add your first one.
              </p>
            )}
            {rows.slice(0, 6).map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-b-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="line-clamp-1 text-[12px] font-medium">{r.title}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {format(parseISO(r.submission_date), "EEE, dd MMM")}
                    {r.brand ? ` · ${r.brand}` : ""}
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium capitalize">
                  {r.status.replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <SubmissionModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        config={config}
      />
    </>
  );
}

function HrDashboard() {
  const { profile } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["hr-dashboard"],
    queryFn: () => getHrDashboard(),
    staleTime: 30_000,
  });
  if (!profile) return null;

  const activeEmployees = data?.activeEmployees ?? 0;
  const STAGES: { key: string; label: string; bg: string; text: string }[] = [
    { key: "applied", label: "Applied", bg: "#EFF6FF", text: "#2563EB" },
    { key: "screening", label: "Screening", bg: "#FFFBEB", text: "#D97706" },
    { key: "interview", label: "Interview", bg: "#F5F3FF", text: "#7C3AED" },
    { key: "offer", label: "Offer", bg: "#ECFDF5", text: "#16A34A" },
    { key: "hired", label: "Hired", bg: "#18181B", text: "#FFFFFF" },
  ];
  const pipeline = data?.pipeline ?? {};
  const pipelineActiveTotal = STAGES.reduce((s, st) => s + (pipeline[st.key] ?? 0), 0);
  const joinings = data?.joinings ?? [];

  return (
    <>
      <AppHeader subtitle={`HR · ${isLoading ? "…" : activeEmployees} active employees`} />
      <div className="flex w-full flex-1 flex-col gap-6 px-6 pb-10 pt-2">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <DarkKpi label="Active Employees" value={isLoading ? "…" : activeEmployees} sub="on roster" />
          <Kpi
            label="On Leave Today"
            value={isLoading ? "…" : (data?.onLeaveToday ?? 0)}
            sub="approved leaves"
            tone="amber"
          />
          <Kpi
            label="Pending Leave Requests"
            value={isLoading ? "…" : (data?.pendingLeaves ?? 0)}
            sub={(data?.pendingLeaves ?? 0) > 0 ? "awaiting review" : "all clear"}
            tone="amber"
            dot={(data?.pendingLeaves ?? 0) > 0}
          />
          <Kpi
            label="Open Positions"
            value={isLoading ? "…" : (data?.openPositions ?? 0)}
            sub="distinct roles"
            tone="blue"
          />
          <Kpi
            label="New Candidates This Week"
            value={isLoading ? "…" : (data?.newCandidatesWeek ?? 0)}
            sub="Mon–today"
            tone="green"
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          <Card>
            <SectionHeader tiny="Recruitment Pipeline" heading="Active candidates by stage" />
            {pipelineActiveTotal === 0 ? (
              <p className="mt-6 text-[12px] text-muted-foreground">No active candidates</p>
            ) : (
              <>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {STAGES.map((st, i) => (
                    <div key={st.key} className="flex items-center gap-2">
                      <div
                        className="rounded-lg px-4 py-3 text-center"
                        style={{ background: st.bg, color: st.text, minWidth: 92 }}
                      >
                        <div className="text-[10px] font-semibold uppercase tracking-[0.05em] opacity-80">
                          {st.label}
                        </div>
                        <div className="mt-1 text-[22px] font-bold leading-none">
                          {pipeline[st.key] ?? 0}
                        </div>
                      </div>
                      {i < STAGES.length - 1 && (
                        <span className="text-muted-foreground">→</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-[12px] text-muted-foreground">
                  {pipelineActiveTotal} total active candidates
                </div>
              </>
            )}
          </Card>
          <Card>
            <SectionHeader tiny="Joining This Month" heading="New hires" />
            <div className="mt-4 flex flex-col gap-3">
              {joinings.length === 0 && (
                <p className="text-[12px] text-muted-foreground">No joinings this month</p>
              )}
              {joinings.slice(0, 5).map((j: { id: string; full_name: string | null; department: string; joining_date: string | null }) => (
                <div
                  key={j.id}
                  className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-b-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <div className="line-clamp-1 text-[12px] font-medium text-[#18181B]">
                      {j.full_name || "—"}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {DEPARTMENT_LABELS[j.department as keyof typeof DEPARTMENT_LABELS] ?? j.department}
                    </div>
                  </div>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {j.joining_date ? format(parseISO(j.joining_date), "dd MMM") : "—"}
                  </span>
                </div>
              ))}
              {joinings.length > 5 && (
                <div className="text-[11px] text-muted-foreground">+{joinings.length - 5} more</div>
              )}
            </div>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid grid-cols-3 gap-3">
            <Tile
              icon={<AlertTriangle className="h-5 w-5" />}
              tone="amber"
              value={isLoading ? "…" : (data?.probationEnding ?? 0)}
              label="Probation ending this month"
            />
            <Tile
              icon={<Clock3 className="h-5 w-5" />}
              tone="amber"
              value={isLoading ? "…" : (data?.pendingAllotments ?? 0)}
              label="Pending asset allotments"
            />
            <Tile
              icon={<CheckCircle2 className="h-5 w-5" />}
              tone="blue"
              value={isLoading ? "…" : activeEmployees}
              label="Total headcount"
            />
          </div>
          <Card>
            <SectionHeader tiny="Recent Admin Activity" heading="Audit log" />
            <div className="mt-4 flex flex-col gap-2">
              {(data?.audit ?? []).length === 0 && (
                <p className="text-[12px] text-muted-foreground">No recent activity</p>
              )}
              {(data?.audit ?? []).map((a: { id: string; action_type: string | null; created_at: string }) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-b-0 last:pb-0"
                >
                  <span className="line-clamp-1 text-[12px] font-medium text-[#18181B]">
                    {(a.action_type ?? "").slice(0, 60)}
                  </span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {format(parseISO(a.created_at), "dd MMM, HH:mm")}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

// ---------- Admin-style shared dashboard helpers ----------

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
      {children}
    </div>
  );
}

function SectionHeader({ tiny, heading }: { tiny: string; heading: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
        {tiny}
      </div>
      <h2 className="mt-1 text-[18px] font-semibold text-[#18181B]">{heading}</h2>
    </div>
  );
}

function DarkKpi({
  label,
  value,
  sub,
}: {
  label: string;
  value: number | string;
  sub: string;
}) {
  return (
    <div className="rounded-xl bg-[#18181B] p-6">
      <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-white/70">
        {label}
      </div>
      <div className="mt-2 text-[36px] font-bold leading-none text-white">{value}</div>
      <div className="mt-2 text-[12px] text-[#16A34A]">{sub}</div>
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
  dot,
}: {
  label: string;
  value: number | string;
  sub: string;
  tone: "green" | "amber" | "blue";
  dot?: boolean;
}) {
  const subColor = tone === "amber" ? "#D97706" : tone === "blue" ? "#2563EB" : "#16A34A";
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-6">
      <div className="flex items-center gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
          {label}
        </div>
        {dot && <span className="h-1.5 w-1.5 rounded-full bg-[#D97706]" />}
      </div>
      <div className="mt-2 text-[36px] font-bold leading-none text-[#18181B]">{value}</div>
      <div className="mt-2 text-[12px]" style={{ color: subColor }}>
        {sub}
      </div>
    </div>
  );
}

function Tile({
  icon,
  tone,
  value,
  label,
  sub,
}: {
  icon: React.ReactNode;
  tone: "blue" | "green" | "red" | "amber" | "gray";
  value: number | string;
  label: string;
  sub?: string;
}) {
  const palette = {
    blue: { bg: "#EFF6FF", border: "#BFDBFE", text: "#2563EB" },
    green: { bg: "#ECFDF5", border: "#A7F3D0", text: "#16A34A" },
    red: { bg: "#FEF2F2", border: "#FECACA", text: "#DC2626" },
    amber: { bg: "#FFFBEB", border: "#FDE68A", text: "#D97706" },
    gray: { bg: "#F9FAFB", border: "#E5E7EB", text: "#374151" },
  }[tone];
  return (
    <div
      className="rounded-xl border p-5"
      style={{ background: palette.bg, borderColor: palette.border }}
    >
      <div style={{ color: palette.text }}>{icon}</div>
      <div className="mt-2 text-[28px] font-bold text-[#18181B]">{value}</div>
      <div className="text-[12px] text-muted-foreground">{label}</div>
      {sub && <div className="mt-1 text-[11px]" style={{ color: palette.text }}>{sub}</div>}
    </div>
  );
}
